import type { UseMutationOptions } from '@tanstack/react-query';
import type { ActionBreadcrumb } from '../plugs/action-breadcrumb';

/**
 * Wraps a `useMutation` options object with breadcrumbs recorded on mutate
 * start, success, and error, tagged with the given business `action` name
 * (e.g. `'reservation.upgradeHard'`). The app decides where the breadcrumbs
 * go (`onActionBreadcrumb` on `AppUiProvider`).
 *
 * Composes with (never replaces) any `onMutate`/`onSuccess`/`onError` already
 * present in `options`: existing side effects (cache invalidation, toasts)
 * still run after the breadcrumb is recorded.
 */
export function withMutationBreadcrumbs<TData, TError, TVariables, TOnMutateResult = unknown>(
  action: string,
  record: ActionBreadcrumb,
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationOptions<TData, TError, TVariables, TOnMutateResult> {
  return {
    ...options,
    onMutate: async (variables, context) => {
      record(`${action}:start`);
      if (options.onMutate) return options.onMutate(variables, context);
      // No original onMutate: callers that rely on onMutate's return value always supply their own.
      return undefined as TOnMutateResult;
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      record(`${action}:success`);
      return options.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      record(`${action}:error`, 'error', {
        message: error instanceof Error ? error.message : String(error),
      });
      return options.onError?.(error, variables, onMutateResult, context);
    },
  };
}
